import React, { useState } from "react";
import {
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  Avatar,
  Box,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect } from "react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
// necessary to import the locale other than default 'en'
import "dayjs/locale/fr";
import dayjs from "dayjs";
import { nationalites } from "../../assets/nationalities";

const PREFIX = "VipInfos";

const classes = {
  root: `${PREFIX}-root`,
  addImageButton: `${PREFIX}-addImageButton`,
  imgFormat: `${PREFIX}-imgFormat`,
  textfieldStyle: `${PREFIX}-textfieldStyle`,
  nameAndSurname: `${PREFIX}-nameAndSurname`,
  fieldsContainer: `${PREFIX}-fieldsContainer`,
  dateAndAge: `${PREFIX}-dateAndAge`,
  ageStyle: `${PREFIX}-ageStyle`,
  selectContainer: `${PREFIX}-selectContainer`,
  dateStyle: `${PREFIX}-dateStyle`,
  genderLabelStyle: `${PREFIX}-genderLabelStyle`,
  presenceTypo: `${PREFIX}-presenceTypo`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.nameAndSurname}`]: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginBottom: "15px",
    },
    [`& .${classes.fieldsContainer}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      alignContent: "space-around",
      alignItems: "center",
      width: "100%",
      maxWidth: "500px",
    },
    [`& .${classes.textfieldStyle}`]: {
      width: "48%",
    },
    [`& .${classes.dateAndAge}`]: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
    },
    [`& .${classes.ageStyle}`]: {
      width: "20%",
    },
    [`& .${classes.selectContainer}`]: {
      width: "100%",
    },
    [`& .${classes.dateStyle}`]: {
      width: "100%",
      marginRight: "1rem",
      marginBottom: "15px",
    },
    [`& .react-select-container`]: {
      height: "56px",
      borderColor: "#D3AF5F",
      marginBottom: "15px",
    },
    [`& .react-select__control`]: {
      height: "56px",
      borderColor: "rgba(211, 175, 95, 0.3) !important",
    },
    [`& .${classes.presenceTypo}`]: {
      display: "flex",
      justifyContent: "flex-start",
      width: "100%",
      marginTop: "0.5rem",
    },
    [`& .${classes.genderLabelStyle}`]: {
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      width: "100%",
    },
  };
});

export default function VipInfos({ updateData, data, canUserModifyData }) {
  const { t } = useTranslation();
  const [age, setAge] = useState("");
  const currentDate = new Date();

  useEffect(() => {
    if (data.vip.birthDate) {
      const tmpDate = new Date(data.vip.birthDate);

      const months = currentDate.getMonth() - tmpDate.getMonth();
      let tmpAge = currentDate.getFullYear() - tmpDate.getFullYear();
      if (
        months < 0 ||
        (months === 0 && currentDate.getDate() < tmpDate.getDate())
      ) {
        tmpAge--;
      }
      setAge(tmpAge);
    }
  }, [data.vip.birthDate]);

  return (
    <StyledDiv>
      <div className={classes.root}>
        <div className={classes.fieldsContainer}>
          <div className={classes.nameAndSurname}>
            <TextField
              id="lastname-input"
              label={t("Crm.surname")}
              value={data.vip.lastname ? data.vip.lastname : ""}
              variant="outlined"
              color="primary"
              inputProps={{ style: { borderColor: "#D3AF5F" } }}
              onChange={(e) => updateData("vip", "lastname", e.target.value)}
              className={classes.textfieldStyle}
              disabled={!canUserModifyData}
            />
            <TextField
              id="firstname-input"
              label={t("Crm.name")}
              value={data.vip.firstname ? data.vip.firstname : ""}
              variant="outlined"
              color="primary"
              onChange={(e) => updateData("vip", "firstname", e.target.value)}
              className={classes.textfieldStyle}
              disabled={!canUserModifyData}
            />
          </div>
          <div className={classes.dateAndAge}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18next.language.substring(0, 2)}
            >
              <DatePicker
                disableFuture
                className={classes.dateStyle}
                label={t("Crm.birthdate")}
                disabled={!canUserModifyData}
                onChange={(e) => {
                  updateData(
                    "vip",
                    "birthDate",
                    e ? dayjs(e).format("YYYY-MM-DD") : null
                  );
                }}
                value={data.vip.birthDate ? dayjs(data.vip.birthDate) : null}
              />
            </LocalizationProvider>
            <TextField
              disabled
              className={classes.ageStyle}
              variant="outlined"
              label="Age"
              value={age ? age : "?"}
            ></TextField>
          </div>
          <div className={classes.selectContainer}>
            <Autocomplete
              disabled={!canUserModifyData}
              id="nationalities-autocomplete"
              options={nationalites}
              getOptionLabel={(option) => option.name}
              disableClearable
              onChange={(e, selected) => {
                updateData("vip", "nationality", selected);
              }}
              value={data.vip.nationality}
              multiple
              isOptionEqualToValue={(option, value) => {
                return option?.cca2 === value?.cca2;
              }}
              noOptionsText={
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {t("configurator.nationalityNotFound")}
                  {/* <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      console.log("Add nationality TBD");
                    }}
                  >
                    {t("configurator.createNationality")}
                  </Button> */}
                </Box>
              }
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  <img
                    loading="lazy"
                    width="20"
                    src={option.flag}
                    alt={`flag-${option?.cca2 || "unknown"}`}
                  />
                  {option.name}
                </Box>
              )}
              renderTags={(tagValue, getTagProps) => {
                return tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    icon={
                      <Avatar
                        alt={option?.flag}
                        src={option?.flag}
                        sx={{ width: 24, height: 24 }}
                      />
                    }
                    label={option?.name}
                  />
                ));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("configurator.autocompleteSelectNationality")}
                  variant="outlined"
                />
              )}
            />
          </div>
          <Typography variant="body1" className={classes.presenceTypo}>
            {t("Crm.genders.presence")}
          </Typography>
          <div className={classes.genderLabelStyle}>
            <FormControlLabel
              control={
                <Checkbox
                  variant="outlined"
                  size="sm"
                  disabled={!canUserModifyData}
                  checked={data.myDesign.men || false}
                  onChange={(e) =>
                    updateData("myDesign", "men", e.target.checked)
                  }
                />
              }
              label={t("Crm.genders.men")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  variant="outlined"
                  size="sm"
                  disabled={!canUserModifyData}
                  checked={data.myDesign.women || false}
                  onChange={(e) =>
                    updateData("myDesign", "women", e.target.checked)
                  }
                />
              }
              label={t("Crm.genders.women")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  variant="outlined"
                  size="sm"
                  disabled={!canUserModifyData}
                  checked={data.myDesign.other || false}
                  onChange={(e) =>
                    updateData("myDesign", "other", e.target.checked)
                  }
                />
              }
              label={t("Crm.genders.others")}
            />
          </div>
        </div>
      </div>
    </StyledDiv>
  );
}
